import {
  type JobCardDataWithBrandingEligibility,
  filterValidBrandingAttribution,
} from "../../jobcard/components/JobCard/types";
import { DatadogLogger } from "../../shared/logging/DatadogLogger";

export interface BrandedJobMigrationProctorAllocation {
  isBrandedJobMigrationTestActive: boolean;
  isBrandedJobMigrationShadow: boolean;
  shouldQueryBrandingEligibility: boolean;
}

export type BrandingEligibility = NonNullable<
  JobCardDataWithBrandingEligibility["brandingEligibility"]
>;

interface ShadowComparableJob {
  key: string;
  employer?: {
    dossier?: {
      images?: {
        squareLogoUrls?: {
          url256: string;
        } | null;
      } | null;
    } | null;
  } | null;
  jobPackagingFeatures?: {
    brandedJobs?: boolean;
    brandingReasons?: string[];
  } | null;
  featuredCardData?: {
    headerImageUrl?: string | null;
  } | null;
}

/**
 * Derives branded job migration flags from proctor allocation.
 * Consolidates the logic for determining test/shadow status and whether to query branding eligibility.
 *
 * @param brandedJobMigrationAllocation - Proctor allocation value (1 = test, 2 = shadow, other = control/inactive)
 * @returns Object containing:
 * - isBrandedJobMigrationTestActive: true if allocation === 1 (test bucket)
 * - isBrandedJobMigrationShadow: true if allocation === 2 (shadow bucket)
 * - shouldQueryBrandingEligibility: true if either test or shadow is active
 */
export const getBrandedJobMigrationFlags = (
  brandedJobMigrationAllocation: number,
): BrandedJobMigrationProctorAllocation => {
  const isBrandedJobMigrationTestActive = brandedJobMigrationAllocation === 1;
  const isBrandedJobMigrationShadow = brandedJobMigrationAllocation === 2;
  const shouldQueryBrandingEligibility = brandedJobMigrationAllocation > 0;

  return {
    isBrandedJobMigrationTestActive,
    isBrandedJobMigrationShadow,
    shouldQueryBrandingEligibility,
  };
};

export const compareJobCardShadowMetrics = (
  job: ShadowComparableJob,
  brandingEligibility: BrandingEligibility,
  surface: "rnhp",
) => {
  const legacyIsBrandedJob = job.jobPackagingFeatures?.brandedJobs ?? false;
  const legacyLogo = job.employer?.dossier?.images?.squareLogoUrls?.url256;
  const legacyReasons = job.jobPackagingFeatures?.brandingReasons ?? [];
  const legacyHeader = job.featuredCardData?.headerImageUrl;

  const subgraphLogo = brandingEligibility.brandingInformation?.localizedAssets?.logo?.url;
  const subgraphHeader = brandingEligibility.brandingInformation?.localizedAssets?.banner?.url;
  const subgraphShouldShowBranding = brandingEligibility.shouldShowBranding ?? false;
  const subgraphAttribution = brandingEligibility.brandingAttribution;

  const eligibilityMismatch = legacyIsBrandedJob !== subgraphShouldShowBranding;
  // Only compare assets if both legacy and subgraph should show branding
  const shouldCompareAssets = !eligibilityMismatch && subgraphShouldShowBranding;

  const logoMismatch = shouldCompareAssets ? legacyLogo !== subgraphLogo : false;

  // Only compare header if legacy header is not null (non-featured jobs do not have header)
  const headerMismatch =
    shouldCompareAssets && legacyHeader != null && legacyHeader !== subgraphHeader;

  // Compare branding reasons
  const validAttribution = filterValidBrandingAttribution(subgraphAttribution);
  const subgraphReasons = mapBrandingAttributionToReasons(validAttribution);
  const normalizedLegacyReasons = [...new Set(legacyReasons)].sort();
  const normalizedSubgraphReasons = [...new Set(subgraphReasons)].sort();

  const reasonMismatch = shouldCompareAssets
    ? normalizedLegacyReasons.length !== normalizedSubgraphReasons.length ||
      !normalizedLegacyReasons.every((reason, i) => reason === normalizedSubgraphReasons[i])
    : false;

  const context: Record<string, unknown> = {
    surface,
    compared: true,
    matched: !eligibilityMismatch && !logoMismatch && !headerMismatch && !reasonMismatch,
    eligibilityMismatch,
    logoMismatch,
    headerMismatch,
    reasonMismatch,
    missingResult: false,
    jobKey: job.key,
  };

  if (eligibilityMismatch) {
    context.legacyIsBrandedJob = legacyIsBrandedJob;
    context.apiShouldShowBranding = subgraphShouldShowBranding;
  }
  if (logoMismatch) {
    context.legacyLogoUrl = legacyLogo ?? null;
    context.apiLogoUrl = subgraphLogo ?? null;
  }
  if (headerMismatch) {
    context.legacyHeaderUrl = legacyHeader ?? null;
    context.apiBannerUrl = subgraphHeader ?? null;
  }
  if (reasonMismatch) {
    context.legacyReasons = normalizedLegacyReasons;
    context.apiReasons = normalizedSubgraphReasons;
  }

  void DatadogLogger.info("branding.shadow.comparison", context);
};

// Map subgraph brandingAttribution source types to legacy brandingReasons strings
// Subgraph source types match legacy brandingReasons strings, so no translation needed
export const mapBrandingAttributionToReasons = (
  brandingAttribution?: Array<{ sourceType: string }>,
): string[] => {
  if (!brandingAttribution) return [];
  return brandingAttribution.map((attribution) => attribution.sourceType);
};

export const handleBrandingMigrationShadow = (
  job: ShadowComparableJob,
  brandingEligibility: BrandingEligibility | null,
  surface: "rnhp",
) => {
  if (!brandingEligibility) {
    const legacyBrandedJobs = job.jobPackagingFeatures?.brandedJobs;
    if (legacyBrandedJobs != null) {
      void DatadogLogger.info("branding.shadow.comparison", {
        surface,
        compared: true,
        missingResult: true,
        jobKey: job.key,
      });
    }
    return;
  }
  compareJobCardShadowMetrics(job, brandingEligibility, surface);
};
